import find from 'lodash/find';
import get from 'lodash/get';
import omit from 'lodash/omit';
import remove from 'lodash/remove';
import map from 'lodash/map';
import unionBy from 'lodash/unionBy';
import union from 'lodash/union';
import filter from 'lodash/filter';
import hasIn from 'lodash/hasIn';
import uniq from 'lodash/uniq';
import forEach from 'lodash/forEach';
import { Mapper } from 'modules/core';
import { Assets, getAsset, uploadAsset } from 'modules/assets';
import { resolveErrorMessage } from 'modules/errors';
import {
  fetchProjects,
  fetchAllProjects,
  fetchRecentlyViewedProjects,
  postRecentlyViewedProject,
  postProject,
  fetchProject,
  patchProject,
  removeProject,
  fetchProjectsStats,
} from './api';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export function loadProjectsAndItsStats(producerId) {
  return async dispatch => {
    dispatch(loadAllProjects({ producerId, sort: null })).then(response => {
      const projects = response.payload.data;
      return dispatch(
        loadProjectsStats({
          producerId,
          projectIds: map(projects, item => get(item, 'id')),
        }),
      );
    });
  };
}

export function loadCovers(coversArray, loadedCoversObj) {
  return dispatch => {
    const loadedCovers = Object.keys(loadedCoversObj);
    const uniqueCovers = filter(uniq(coversArray), el => {
      if (find(loadedCovers, el)) {
        return null;
      }
      return el;
    });
    const nonLoadedCovers = filter(uniqueCovers, null);

    forEach(nonLoadedCovers, cover => {
      dispatch(loadCover(cover));
    });
  };
}

export function uploadProjectImage(producerId, projectId, imageFile) {
  if (!producerId || !projectId) {
    return Promise.reject("Don't have producer ID or project ID.");
  }

  return dispatch => {
    const context = Assets.createProjectContext(producerId, projectId);
    return dispatch(
      uploadAsset({
        imageFile,
        assetType: 'image',
        assetClass: 'projectImage',
        context,
      }),
    );
  };
}

export function updateProject(producerId, projectId, values) {
  return dispatch => {
    const { coverImage } = values;

    if (coverImage) {
      return dispatch(
        uploadProjectImage(producerId, projectId, coverImage),
      ).then(response => {
        const projectValues = { ...values, coverImage: response.payload };
        return dispatch(
          update({ producerId, projectId, values: projectValues }),
        );
      });
    }

    return dispatch(update({ producerId, projectId, values }));
  };
}

export function createProject(producerId, values) {
  return dispatch => {
    const { coverImage } = values;

    if (coverImage) {
      return dispatch(
        create({ producerId, values: omit(values, 'coverImage') }),
      ).then(response => {
        const newProjectId = get(response.payload, 'data.id');

        return dispatch(
          uploadProjectImage(producerId, newProjectId, coverImage),
        ).then(response => {
          const projectValues = { ...values, coverImage: response.payload };
          return dispatch(
            update({
              producerId,
              projectId: newProjectId,
              values: projectValues,
            }),
          );
        });
      });
    }

    return dispatch(create({ producerId, values }));
  };
}

export function initializeProjectsPage(producerId) {
  if (!producerId) {
    return Promise.reject();
  }

  return dispatch => {
    const recentlyViewedCall = dispatch(loadRecentlyViewedProjects(producerId));
    const projectsCall = dispatch(loadAllProjects({ producerId, sort: null }));

    return Promise.all([projectsCall, recentlyViewedCall]).then(responses => {
      const loadedProjects = get(responses, '[0].payload.data');
      const loadedRecentlyViewed = get(responses, '[1].payload.data');

      const projectsIds = map(loadedProjects, 'id');
      const recentlyViewedProjectsIds = map(
        loadedRecentlyViewed,
        recentlyViewedProject => get(recentlyViewedProject, 'project.id'),
      );

      return dispatch(
        loadProjectsStats({
          producerId,
          projectIds: union(projectsIds, recentlyViewedProjectsIds),
        }),
      );
    });
  };
}

export const loadAllProjects = createAsyncThunk(
  'projects/loadAll',
  async ({ producerId, sort }) => await fetchAllProjects(producerId, sort),
);
export const loadProjects = createAsyncThunk(
  'projects/load',
  async producerId => await fetchProjects(producerId, null),
);
export const loadProjectsStats = createAsyncThunk(
  'projects/loadStats',
  async ({ producerId, projectIds }) =>
    await fetchProjectsStats(producerId, projectIds),
);

export const loadProject = createAsyncThunk(
  'projects/loadProject',
  async ({ producerId, projectId }) =>
    await fetchProject(producerId, projectId),
);
export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async ({ producerId, projectId }) =>
    await removeProject(producerId, projectId),
);
export const loadCover = createAsyncThunk(
  'projects/loadCover',
  async assetPath => await getAsset(assetPath),
);

export const loadRecentlyViewedProjects = createAsyncThunk(
  'projects/loadRecentlyViewed',
  async producerId => await fetchRecentlyViewedProjects(producerId),
);
export const createRecentlyViewedProject = createAsyncThunk(
  'projects/createRecentlyViewed',
  async ({ producerId, projectId }) =>
    await postRecentlyViewedProject(producerId, projectId),
);

// TODO: ???????
// export const setSort = createThunk(
//   sort => {
//     return sort;
//   },
//   'set-sort',
//   'projects',
// );

export const setSort = createAsyncThunk('projects/setSort', async sort => sort);

const update = createAsyncThunk(
  'projects/update',
  async ({ producerId, projectId, values }) =>
    await patchProject(producerId, projectId, values),
);

const create = createAsyncThunk(
  'projects/create',
  async ({ producerId, values }) => await postProject(producerId, values),
);

const projectsSlice = createSlice({
  name: 'projects',
  initialState: {
    sort: '-createdAt',
    ownerAvatar: null,
    covers: {},
    loadingCovers: [],
    loadingRecentlyViewed: false,
    loading: false,
    error: false,

    // TODO: make project object in state deprecated
    project: null,

    // TODO: turn arrays of data to object maps (AHAB-597)
    projects: [],
    recentlyViewedProjects: [],
    stats: [],

    // TODO: Move stats and recently viewed projects into separate modules (AHAB-596)
    loadingProjectsStats: false,
    filterData: null,
  },
  reducers: {
    removeError(state, _) {
      state.error = false;
    },
    setProjectsFilterData(state, action) {
      state.filterData = { ...action.payload };
    },
    clearProjectsFilterData(state, _) {
      state.filterData = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadAllProjects.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadAllProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.data;
      })
      .addCase(loadAllProjects.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(loadProjects.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadProjects.fulfilled, (state, action) => {
        const newProjects = action.payload.data;

        state.loading = false;
        state.projects = unionBy(newProjects, state.projects, 'id');
      })
      .addCase(loadProjects.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(create.fulfilled, (state, action) => {
        const newProject = action.payload.data;

        state.project = newProject;
        state.projects = [...state.projects, newProject];
      })
      .addCase(create.rejected, (state, action) => {
        state.error = resolveErrorMessage(action.error);
      })
      .addCase(loadProject.fulfilled, (state, action) => {
        const newProject = action.payload.data;

        state.loading = false;
        state.projects = Mapper.addOrReplace(state.projects, newProject);
        state.project = action.payload.data;
      })
      .addCase(update.pending, (state, _) => {
        state.error = false;
      })
      .addCase(update.fulfilled, (state, action) => {
        const newProject = action.payload.data;

        state.projects = Mapper.addOrReplace(state.projects, newProject);
        state.project = newProject;
        state.error = false;
      })
      .addCase(update.rejected, (state, action) => {
        state.error = resolveErrorMessage(action.error);
      })
      // Not currently in use
      .addCase(deleteProject.fulfilled, (state, action) => {
        const deletedProjectId = action.meta.arg.projectId;

        remove(state.projects, project => project.id === deletedProjectId);
        remove(
          state.recentlyViewedProjects,
          project => project.projectId === deletedProjectId,
        );

        state.project = null;
        state.error = false;
      })
      .addCase(setSort.fulfilled, (state, action) => {
        state.sort = action.payload;
      })
      .addCase(loadRecentlyViewedProjects.pending, (state, _) => {
        state.loadingRecentlyViewed = true;
      })
      .addCase(loadRecentlyViewedProjects.fulfilled, (state, action) => {
        state.recentlyViewedProjects = action.payload.data;
        state.loadingRecentlyViewed = false;
      })
      .addCase(loadRecentlyViewedProjects.rejected, (state, _) => {
        state.loadingRecentlyViewed = false;
      })
      .addCase(createRecentlyViewedProject.fulfilled, (state, action) => {
        const newProject = action.payload.data;

        state.recentlyViewedProjects = [
          ...state.recentlyViewedProjects,
          newProject,
        ];
      })
      .addCase(loadProjectsStats.pending, (state, _) => {
        state.loadingProjectsStats = true;
      })
      .addCase(loadProjectsStats.fulfilled, (state, action) => {
        state.loadingProjectsStats = false;
        state.stats = action.payload.data;
      })
      .addCase(loadProjectsStats.rejected, (state, _) => {
        state.loadingProjectsStats = false;
      })
      .addCase(loadCover.pending, (state, action) => {
        state.loadingCovers = [...state.loadingCovers, action.meta.arg];
      })
      .addCase(loadCover.fulfilled, (state, action) => {
        if (!hasIn(state.covers, action.meta.arg)) {
          const requestUrl = action.meta.arg;
          const fileBlob = action.payload;
          state.covers = {
            ...state.covers,
            [requestUrl]: fileBlob,
          };
        }

        remove(state.loadingCovers, el => {
          return el === action.meta.arg;
        });
      })
      .addCase(loadCover.rejected, (state, action) => {
        if (!hasIn(state.covers, action.meta.arg)) {
          const requestUrl = action.meta.arg;
          state.covers = {
            ...state.covers,
            [requestUrl]: null,
          };
        }
        remove(state.loadingCovers, el => {
          return el === action.meta.arg;
        });
      });
  },
  selectors: {
    getSort: state => state.sort,
    getProjects: state => state.projects,
    getProjectsStats: state => state.stats,
    getLoadingProjectsStats: state => state.loadingProjectsStats,
    getProject: state => state.project,
    getRecentlyViewedProjects: state => state.recentlyViewedProjects,
    getLoading: state => state.loading,
    getLoadingRecentlyViewed: state => state.loadingRecentlyViewed,
    getError: state => state.error,
    getProjectsFilterData: state => state.filterData,
    getCovers: state => state.covers,
    getLoadingCovers: state => state.loadingCovers,
  },
});

export const reducer = projectsSlice.reducer;
export const { removeError, setProjectsFilterData, clearProjectsFilterData } =
  projectsSlice.actions;
export const {
  getSort,
  getProjects,
  getProjectsStats,
  getLoadingProjectsStats,
  getProject,
  getRecentlyViewedProjects,
  getLoading,
  getLoadingRecentlyViewed,
  getError,
  getProjectsFilterData,
  getCovers,
  getLoadingCovers,
} = projectsSlice.selectors;

export const getProjectById = projectId =>
  createSelector(getProjects, getProject, (stateProjects, stateProject) => {
    if (stateProject && stateProject.id === projectId) {
      return stateProject;
    }

    return find(stateProjects, { id: projectId });
  });

export const getProjectsWithActiveAuditions = createSelector(
  getProjects,
  getProjectsStats,
  (projects, projectStats) =>
    filter(
      projects,
      project =>
        get(
          find(
            projectStats,
            projectStat => get(projectStat, 'projectId') === get(project, 'id'),
          ),
          'activeAuditionCount',
        ) > 0,
    ),
);

export const getCoverImage = coverPath =>
  createSelector(getCovers, stateCovers => get(stateCovers, coverPath));
